import { Box, Button, IconButton, VStack } from "@chakra-ui/react"
import { AugmentationPanelRow } from "@pathwright/ux/src/components/augmentation/AugmentationPanelRow"
import PanelSection, {
  AugmentationPanelItemProps
} from "@pathwright/ux/src/components/augmentation/AugmentationPanelSection"
import {
  AUGMENTATION_PANEL_ACTION_BUTTON_PROPS,
  AUGMENTATION_PANEL_MENU_ITEM_ICON_CSS,
  AUGMENTATION_PANEL_SWITCH_CONTROL_PROPS
} from "@pathwright/ux/src/components/augmentation/augmentationPanelStyles"
import Link from "@pathwright/ux/src/components/link/Link"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import { useMemo } from "react"
import { useUserSchoolSubscriptionQuery } from "@pathwright/graphql-client/src/generated"
import Pathicon from "../../pathicon/Pathicon"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import ProfileUserCard from "../../profile/ProfileUserCard"
import getStripePaymentPortalHref from "../../stripe/utils/getStripePaymentPortalHref"
import { getIsSubscriptionActive } from "../../subscription/utils"
import { HomeScreenViewToggle } from "../../unified-home/view-permission/HomeScreenViewToggle"
import useUnifiedHomePermissions from "../../unified-home/view-permission/useUnifiedHomePermissions"
import { useUserCommunityContext } from "../../user/UserCommunityContext"
import HomeSettingsNotifications from "./UserAugmentationNotificationsSettings"
import { useUserPaymentHistoryItems } from "./UserAugmentationPaymentHistory"

function useManageSubscriptionItem(): AugmentationPanelItemProps | null {
  const { t } = useTranslate()
  const { communityGroupSubscriptions, isCommunityGroupOwner } =
    useUserCommunityContext()
  const isCommunityGroupSubscriptionMember =
    communityGroupSubscriptions.length > 0

  const userSubscriptionQuery = useUserSchoolSubscriptionQuery()
  const userSubscription =
    userSubscriptionQuery.data?.me?.school_subscription?.subscription
  const isSchoolSubscriptionOwner = getIsSubscriptionActive(userSubscription)

  if (!isCommunityGroupSubscriptionMember && !isSchoolSubscriptionOwner) {
    return null
  }

  return {
    itemKey: "manage-subscription",
    icon: "credit-card",
    label: t("Manage subscription"),
    ...(isCommunityGroupOwner
      ? { href: getStripePaymentPortalHref() }
      : {
          to: isCommunityGroupSubscriptionMember
            ? "/subscribe"
            : "/manage/billing"
        })
  }
}

function UserAugmentation() {
  const { t } = useTranslate()
  const { me } = usePathwrightContext()
  const hasPaymentHistory = useUserPaymentHistoryItems()?.length > 0
  const manageSubscriptionItem = useManageSubscriptionItem()
  const { canPreview } = useUnifiedHomePermissions()

  const destinationItems = useMemo(() => {
    const items: AugmentationPanelItemProps[] = []

    if (hasPaymentHistory) {
      items.push({
        itemKey: "payment-history",
        icon: "credit-card",
        label: t("profile.history", "View payment history"),
        panelRoute: "payment-history"
      })
    }

    if (manageSubscriptionItem) {
      items.push(manageSubscriptionItem)
    }

    items.push({
      itemKey: "password",
      icon: "lock",
      label: me.no_password_set
        ? t("profile.create_password", "Create password")
        : t("profile.password", "Change password"),
      panelRoute: "password"
    })

    return items
  }, [hasPaymentHistory, manageSubscriptionItem, me.no_password_set, t])

  return (
    <VStack w="100%" minH="100%" gap="augmentationPanel" align="stretch">
      <Box pos="relative" px="augmentationPanelGutter" pt="augmentationPanel">
        <ProfileUserCard user={me} />
        <Link asChild relativeTo="profile">
          <IconButton
            aria-label="Edit Profile"
            size="2xs"
            variant="ghost"
            rounded="full"
            cursor="pointer"
            pos="absolute"
            top={6}
            right={4}
          >
            <Pathicon
              icon="edit"
              size={16}
              css={AUGMENTATION_PANEL_MENU_ITEM_ICON_CSS}
            />
          </IconButton>
        </Link>
      </Box>

      <PanelSection.Group>
        <PanelSection.Root>
          <PanelSection.Content>
            <VStack align="stretch" gap="5px" w="full">
              {canPreview ? (
                <AugmentationPanelRow
                  interactive={false}
                  css={{
                    "&:has(.chakra-switch__root)": {
                      bg: "augmentationPanel.fill.subtle"
                    },
                    "& .chakra-switch__control":
                      AUGMENTATION_PANEL_SWITCH_CONTROL_PROPS
                  }}
                >
                  <HomeScreenViewToggle />
                </AugmentationPanelRow>
              ) : null}
              <HomeSettingsNotifications />
              <PanelSection.List items={destinationItems} />
            </VStack>
          </PanelSection.Content>
        </PanelSection.Root>
      </PanelSection.Group>

      <Box px="augmentationPanelGutter" pb="augmentationPanel">
        <Button
          asChild
          {...AUGMENTATION_PANEL_ACTION_BUTTON_PROPS}
          bg="augmentationPanel.fill.muted"
          color="augmentationPanel.text"
          _hover={{ bg: "augmentationPanel.fill.strong" }}
          w="full"
        >
          <Link to="/auth/sign-out/">
            <Pathicon icon="arrow-up-line" transform="rotate(-90deg)" />
            {t("nav.navbar_user.sign_out", "Sign out")}
          </Link>
        </Button>
      </Box>
    </VStack>
  )
}

export default UserAugmentation
